import { PartialNullable } from "../Extensions";
import { ObjectHelper } from "../Helpers/ObjectHelper";
import { ObservableArray } from "../ObservableArray";
import { PropertyChange } from "../PropertyChange";
import { IRadObject, Property, PropertyObject, RadObject, RadObjectPropertyValues } from "../RadObject";
import { DataSourcePropertyValueChange } from "./DataSourcePropertyValueChange";
import { NAMESPACE } from "./_Namespace";

export type DataSourcePropertyValues =
    RadObjectPropertyValues
    & {
        data: any;
        dataOriginal: any;
        validationErrors: any;
    };

export type DataSourceProperties = {
    [name in keyof DataSourcePropertyValues]: Property<DataSourcePropertyValues[name]>
};

export interface IDataSource extends IRadObject, DataSourceProperties
{
    acceptChanges(): void;
    propertyValueByPathGet<T extends bigint | boolean | Function | null | number | object | string | symbol | undefined = any>(path: string): T | undefined;
    propertyValueByPathSet<T extends bigint | boolean | Function | null | number | object | string | symbol | undefined = any>(path: string, value: T, inhibitEvent?: boolean): boolean;
    revertChanges(): void;
}

export class DataSource extends RadObject<DataSourcePropertyValues> implements IDataSource
{
    public static override classNamespace = NAMESPACE + "DataSource";
    public static override className = "DataSource";

    // #region Properties

    public data!: PropertyObject;
    public dataOriginal!: PropertyObject;
    public validationErrors!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<DataSourcePropertyValues>)
    {
        super(propertyValues);

        // Trigger Property Changes
        this.data.triggerChange();
        this.validationErrors.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public acceptChanges(): void
    {
        // TODO: expand to handled nested objects
        ObjectHelper.copySimpleProperties(this.data.get(), this.dataOriginal.get());
    }
    public propertyValueByPathGet<T extends bigint | boolean | Function | null | number | object | string | symbol | undefined = any>(path: string): T | undefined
    {
        const
            data = this.data.get();

        if (!data)
            throw new Error("The data source contains no data.");

        return ObjectHelper.propertyValueByPathGet(data, path);
    }
    public propertyValueByPathSet<T extends bigint | boolean | Function | null | number | object | string | symbol | undefined = any>(path: string, value: T, inhibitEvent?: boolean): boolean
    {
        const
            data = this.data.get();

        if (!data)
            throw new Error("The data source contains no data.");

        const
            originalValue = this.propertyValueByPathGet(path);

        if (ObjectHelper.propertyValueByPathSet(data, path, value))
        {
            if (!inhibitEvent)
            {
                this.triggerHandler("propertyValueChanged", new DataSourcePropertyValueChange<any>(this, path, this.propertyValueByPathGet(path), originalValue));
                return true;
            }
        }

        return false;
    }
    public revertChanges(): void
    {
        // TODO: expand to handled nested objects
        const
            dataOriginal = this.dataOriginal.get();

        for (let property in dataOriginal)
        {
            if (!ObjectHelper.isNullOrUndefined(dataOriginal[property]) && ObjectHelper.typeOf(dataOriginal[property]) === "object")
                continue;

            this.propertyValueByPathSet(property, dataOriginal[property]);
        }
    }

    // #endregion

    // #region Protected Methods

    protected _dataChanged(change: PropertyChange<any>)
    {
        this.dataOriginal.set(
            change.currentValue
                ?
                (
                    change.currentValue instanceof ObservableArray
                        ? ObservableArray.fromArray(change.currentValue)
                        : ObjectHelper.cloneObject(change.currentValue)
                )
                : null
        );
    }
    protected _dataOriginalChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _validationErrorsChanged(change: PropertyChange<any>)
    {
        this.triggerHandler("validationErrorsChanged", change);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: DataSourcePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.data = new PropertyObject(this, propertyValues.data, this._dataChanged);
        this.dataOriginal = new PropertyObject(this, propertyValues.dataOriginal, this._dataOriginalChanged);
        this.validationErrors = new PropertyObject(this, propertyValues.validationErrors, this._validationErrorsChanged);
    }

    // #endregion
}
